import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fromHex } from "@redchain/input-log";
import { api, isApiError } from "../api/client";
import { buildMatchesDeploy } from "../buildId";
import { KeymapEditor } from "../components/KeymapEditor";
import { RegisterStep } from "../components/RegisterStep";
import { RomPicker } from "../components/RomPicker";
import { TouchControls } from "../components/TouchControls";
import { LinkStatusBar } from "../link/LinkStatusBar";
import { commitUnion } from "../link/unionMatch";
import type { RomRecord } from "../main/db";
import { computeCoreBuildHash, EmulatorHost } from "../main/EmulatorHost";
import { LinkController, type LinkResult, type LinkView } from "../main/LinkController";
import { type PaletteName } from "../main/Renderer";
import { storedRom, versionLabel } from "../main/RomLoader";
import { sessionRom } from "../main/romPatch";
import { SessionClient, uuidToBytes } from "../main/SessionClient";
import { Overlay } from "../overlay/Overlay";
import { initialOverlayState, overlayReducer } from "../overlay/state";
import { SyncPendingReporter } from "../overlay/useWalletAction";
import { lockFor } from "../locks";
import { usePlayer } from "../state/PlayerContext";
import { autosaveText, purseText, t } from "../strings";
import { CableClubBattleBoard, CableClubInvite, CableClubTradeBoard, CableClubWaiting, type BattleRow, type TradeRow, type TrainerRow, type ClubInvite, type OutgoingRequest } from "../wallet-menu/CableClubBoard";
import { WalletMenu } from "../wallet-menu/WalletMenu";
import { useWallet } from "../wallet/WalletProvider";
import { readableChainError } from "../chain-error";
import { GameControls, FullscreenButton } from "../game-shell/GameControls";
import { ForestModal } from "../game-shell/ForestModal";
import { MobileNavigation, type MobileView } from "../game-shell/MobileNavigation";
import { ChainRail, InventoryRail } from "../game-shell/GameShell";
import { readInventory, type Inventory } from "../game-shell/inventory";

type Step =
  | { kind: "loading" }
  | { kind: "register" }
  | { kind: "rom" }
  | { kind: "draining"; remaining: number }
  | { kind: "starting" }
  | { kind: "playing"; offline: boolean }
  | { kind: "superseded" }
  | { kind: "error"; message: string };

/**
 * /play — boot flow WEB-12: login → register if needed → ROM → drain pending uploads →
 * session start → worker init with the composed SRAM and the server nonce in the PKIL header.
 */
export function Play() {
  const { wallet, auth, ready } = useWallet();
  const walletAddress = wallet?.address ?? null;
  const { me, refresh, setInSession, lastError } = usePlayer();
  const cableLock = lockFor(me, "cable_club");
  const [step, setStep] = useState<Step>({ kind: "loading" });
  const [inventory, setInventory] = useState<Inventory | null>(null);
  const [liveParty, setLiveParty] = useState<{ species: number; level: number }[] | null>(null);
  const [rom, setRom] = useState<RomRecord | null>(null);
  const [lockedVersion, setLockedVersion] = useState<RomRecord["version"] | null>(null);
  const [overlay, dispatch] = useReducer(overlayReducer, initialOverlayState);
  const [showKeys, setShowKeys] = useState(false);
  const [showPowerCycle, setShowPowerCycle] = useState(false);
  const [mobileView, setMobileView] = useState<MobileView>("game");
  useEffect(() => {
    const query = window.matchMedia("(max-width: 900px), (pointer: coarse) and (max-width: 1200px)");
    const update = () => { if (!query.matches) setMobileView("game"); };
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  const playRef = useRef<HTMLElement>(null);
  const [fullscreenFallback, setFullscreenFallback] = useState(false);
  const toggleFullscreen = async () => {
    if (document.fullscreenElement) { await document.exitFullscreen(); return; }
    if (fullscreenFallback) { setFullscreenFallback(false); return; }
    try { await playRef.current?.requestFullscreen(); }
    catch { setFullscreenFallback(true); }
  };
  const [palette, setPalette] = useState<PaletteName>("native");
  const [touch, setTouch] = useState(() => window.matchMedia?.("(pointer: coarse)").matches ?? false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const screenRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<EmulatorHost | null>(null);
  const sessionRef = useRef<SessionClient | null>(null);
  const linkRef = useRef<LinkController | null>(null);
  const [link, setLink] = useState<{ view: LinkView; result: LinkResult | null }>({ view: { kind: "idle" }, result: null });
  const [walletOpen, setWalletOpen] = useState(false);
  const [boardOpen, setBoardOpen] = useState(false);
  const [boardKind, setBoardKind] = useState<1 | 2>(1);
  const [battles, setBattles] = useState<BattleRow[]>([]);
  const [trades, setTrades] = useState<TradeRow[]>([]);
  const [myBattles, setMyBattles] = useState<BattleRow[]>([]);
  const [myTrades, setMyTrades] = useState<TradeRow[]>([]);
  const [trainers, setTrainers] = useState<TrainerRow[]>([]);
  const [boardNotice, setBoardNotice] = useState<string>();
  const [invite, setInvite] = useState<ClubInvite | null>(null);
  const [outgoing, setOutgoing] = useState<OutgoingRequest | null>(null);
  const previousInvite = useRef<ClubInvite | null>(null);
  const previousOutgoing = useRef<OutgoingRequest | null>(null);
  const boardOpenRef = useRef(boardOpen); boardOpenRef.current = boardOpen;
  const boardReadVersion = useRef(0);
  const refreshBoard = async () => {
    const version = ++boardReadVersion.current;
    const view = await api.cableClubBoard();
    if (version !== boardReadVersion.current) return null;
    setBattles(view.battles); setTrades(view.trades);
    setMyBattles(view.myBattles ?? []); setMyTrades(view.myTrades ?? []);
    setTrainers(view.trainers);
    setOutgoing(view.outgoing ?? null);
    if (previousOutgoing.current && !view.outgoing && !view.match) setBoardNotice("Request ended or was declined. You can send another.");
    previousOutgoing.current = view.outgoing ?? null;
    return view;
  };
  const readBoardParty = async () => (await api.cableClubParty()).slots;
  const cancelListing = async (id: string) => {
    ++boardReadVersion.current;
    try { await api.cableClubCancel(id); setBoardNotice("Post cancelled."); }
    finally { await refreshBoard(); }
  };
  const sendRequest = async (id: string, slot?: number) => {
    ++boardReadVersion.current;
    try { await api.cableClubRequest(id, slot); setBoardNotice(undefined); }
    finally { await refreshBoard(); }
  };
  const sendInvite = async (playerId: string, kind: "battle" | "trade") => {
    try { await api.cableClubInvite(playerId, kind); setBoardNotice(undefined); }
    finally { await refreshBoard(); }
  };
  const sharedLobby = useRef(new Set<string>());
  const declineInvite = () => {
    void api.cableClubDeclineInvite().then(() => {
      setInvite(null); previousInvite.current = null;
      hostRef.current?.releaseInvite();
      if (boardOpenRef.current) hostRef.current?.openBoardUi();
    }).catch((err: unknown) => setBoardNotice(err instanceof Error ? err.message : "Couldn't decline."));
  };
  const beginUnion = (lobbyId: string, mode: "battle" | "trade", dest: 1 | 2, release: () => void) => {
    if (sharedLobby.current.has(lobbyId)) return;
    const started = commitUnion(
      () => linkRef.current?.armBeforeUnion({ lobbyId, code: "", hostMode: "lockstep", mode }) ?? false,
      () => hostRef.current?.acceptUnion(lobbyId, dest),
    );
    if (!started) return;
    sharedLobby.current.add(lobbyId);
    release();
  };
  useEffect(() => {
    if (step.kind !== "playing") return;
    let stop = false;
    const pull = async () => {
      try {
        const here = await hostRef.current?.readParty();
        if (stop) return;
        if (here) setLiveParty(here.slots);
        if (here && here.map !== 0) await api.cableClubHeartbeat(here.map, here.trainer);
        const view = await refreshBoard();
        if (stop || !view) return;
        const nextInvite = here?.map === 0xee && !linkRef.current?.active ? view.invite ?? null : null;
        if (previousInvite.current && !nextInvite) {
          hostRef.current?.releaseInvite();
          if (boardOpenRef.current) hostRef.current?.openBoardUi();
        }
        previousInvite.current = nextInvite; setInvite(nextInvite);
        const match = view.match;
        if (!match || here?.map !== 0xee || sharedLobby.current.has(match.lobbyId)) return;
        beginUnion(match.lobbyId, match.dest === 2 ? "battle" : "trade", match.dest, () => {
          setBoardOpen(false);
          setInvite(null);
          hostRef.current?.closeBoardUi();
          hostRef.current?.releaseInvite();
        });
      } catch {
        // The next poll retries. A missed poll does not open a lobby.
      }
    };
    void pull();
    const timer = window.setInterval(() => void pull(), 2000);
    return () => {
      stop = true;
      window.clearInterval(timer);
    };
    // beginUnion closes over refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.kind]);
  const booting = useRef(false);

  // A tab left open across a deploy is a mix of old and new files. Reload before it records another session.
  useEffect(() => {
    if (step.kind !== "playing") return;
    const timer = window.setInterval(() => {
      void buildMatchesDeploy().then((ok) => {
        if (ok) return;
        hostRef.current?.superseded();
        sessionRef.current?.stop();
        window.location.reload();
      });
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [step.kind]);

  // 1–3: player record, registration, ROM (with the generation's version lock).
  const prepare = useCallback(async () => {
    const me = await refresh();
    if (!me) {
      // Offline: boot from the last canonical SRAM on this device (§7.5) if we have a ROM.
      const local = navigator.onLine ? null : await storedRom(null);
      if (local) return setRom(local);
      const detail = lastError()?.trim();
      const rateLimited = detail && /rate.?limit|too many|rpc_rate|limit \d+ per/i.test(detail);
      return setStep({
        kind: "error",
        message: rateLimited
          ? `${detail}. Wait about a minute, then reload — don't spam Reload or you'll hit the session limit again.`
          : detail
            ? `Couldn't load your player account: ${readableChainError(detail)}`
            : "Couldn't load your player account. Check your connection and reload.",
      });
    }
    if (!me.registered) return setStep({ kind: "register" });
    setLockedVersion(me.gameVersion);
    dispatch({ type: "init", lastSyncedAt: me.lastSyncedAt ? Date.parse(me.lastSyncedAt) : null, offline: false, vault: me.vault });
    const stored = await storedRom(me.gameVersion);
    if (stored) setRom(stored);
    else setStep({ kind: "rom" });
  }, [refresh, lastError]);

  useEffect(() => {
    if (!auth || step.kind !== "loading") return;
    void prepare();
  }, [auth, step.kind, prepare]);

  // 4–6: drain, session start, worker boot.
  useEffect(() => {
    // Keyed on the address: re-signing in with the same wallet rebuilds the wallet object mid-boot.
    if (!rom || !walletAddress || booting.current) return;
    booting.current = true;
    const sc = new SessionClient(api, walletAddress);
    sessionRef.current = sc;
    let cancelled = false;

    (async () => {
      sc.onOfflineResult = (result) => {
        dispatch({ type: "offline", result });
        if (result.kind === "adopted" && result.live && !cancelled) setStep((s) => (s.kind === "playing" ? { kind: "playing", offline: false } : s));
      };
      setStep({ kind: "draining", remaining: 0 });
      const drained = await sc.drainPending((remaining) => setStep({ kind: "draining", remaining }));
      if (cancelled) return;

      setStep({ kind: "starting" });
      if (!(await buildMatchesDeploy())) {
        window.location.reload();
        return;
      }
      const coreBuildHash = await computeCoreBuildHash();
      const started = await sc.start({ romSha1: rom.sha1, coreBuildHash });
      if (cancelled) return sc.stop();
      if (started.gameVersion && started.gameVersion !== rom.version) {
        sc.stop();
        setRom(null);
        setLockedVersion(started.gameVersion);
        setStep({ kind: "rom" });
        booting.current = false;
        return;
      }

      sc.onQueue = (q) => dispatch({ type: "queue", pendingCheckpoints: q.pendingCheckpoints, online: q.online });
      sc.onCheckpointUploaded = () => dispatch({ type: "checkpointUploaded" });
      // Purse credits for this session go into the running game; ones that arrive before the worker is up wait for it.
      const earlyCredits: { creditId: string; amount: number }[] = [];
      sc.onEvent = (event) => {
        dispatch({ type: "sse", event });
        if (event.type === "purse.credit" && event.sessionId === started.sessionId) {
          if (hostRef.current) hostRef.current.purseCredit(event.creditId, event.amount);
          else earlyCredits.push(event);
        }
        // vault.changed is counted before the save's mints land; the API's count after the commit is current.
        // Mints are paid in ₽, so a deposit or refund changes what they still need.
        if (event.type === "chain.committed" || event.type === "rcd.deposited" || event.type === "rcd.refunded")
          void refresh().then((me) => {
            if (me?.vault) dispatch({ type: "vault", vault: me.vault });
          });
      };
      sc.onSuperseded = () => {
        hostRef.current?.superseded();
        dispatch({ type: "superseded" });
        setStep({ kind: "superseded" });
      };
      dispatch({ type: "notices", notices: started.notices });
      if (started.offline || drained === "offline") dispatch({ type: "init", lastSyncedAt: overlay.lastSyncedAt, offline: true });

      const canvas = canvasRef.current;
      const screen = screenRef.current;
      if (!canvas || !screen) throw new Error("screen not mounted");
      const host = new EmulatorHost(canvas, screen, sc, {
        onInventorySave: (sram) => setInventory(readInventory(sram)),
        onAutosave: (m) => dispatch({ type: "autosave", text: autosaveText(m), now: Date.now() }),
        onPurseApplied: (m) => dispatch({ type: "autosave", text: purseText(m), now: Date.now() }),
        onWalletOpen: () => {
          host.openWalletUi();
          setWalletOpen(true);
        },
        onBoardOpen: (board) => {
          host.openBoardUi();
          setBoardKind(board);
          setBoardOpen(true);
        },
        onError: (message) => setStep({ kind: "error", message: `The emulator stopped: ${message}` }),
      });
      hostRef.current = host;
      setInventory(readInventory(started.sram));
      setPalette(host.renderer.getPalette());
      await host.start({
        rom: sessionRom(rom.bytes, rom.version, started.romPatch),
        sram: started.sram,
        sessionHeader: {
          sessionId: uuidToBytes(started.sessionId),
          coreBuildHash: fromHex(coreBuildHash),
          romSha1: fromHex(rom.sha1),
          startSramSha256: started.sramSha256,
          serverNonce: started.serverNonce,
        },
        autosaveTriggers: started.autosaveTriggers,
      });
      for (const c of earlyCredits.splice(0)) host.purseCredit(c.creditId, c.amount);
      const lc = new LinkController(host, sc, api, rom.version, (view, result) => setLink({ view, result }));
      linkRef.current = lc;
      setInSession(true);
      setStep({ kind: "playing", offline: started.offline });
    })().catch((e: unknown) => {
      // A failed boot (session start, offline fallback or worker) must not leave uploads, SSE or heartbeats running.
      sc.stop();
      if (cancelled) return;
      booting.current = false;
      let message = isApiError(e) ? (e.body.message ?? e.body.reason) : e instanceof Error ? e.message : String(e);
      // Pino's err serializer used to mask real API failures as this TypeError in the HTTP body.
      if (/circular-ref-tag|object is not extensible/i.test(message) || message === "internal" || message === "500 internal") {
        message = "Couldn't start the session. Reload and try again; if it keeps failing, your last save may still be processing.";
      }
      if (isApiError(e, "rate_limited") || isApiError(e, "rpc_rate_limited") || /limit \d+ per|too many requests|rpc_rate/i.test(message)) {
        message = `${message}. Wait about a minute before reloading — rapid retries make it worse.`;
      }
      setStep({ kind: "error", message });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rom, walletAddress]);

  // Teardown on leaving /play.
  useEffect(
    () => () => {
      setInSession(false);
      linkRef.current?.cancel();
      linkRef.current?.dispose();
      linkRef.current = null;
      hostRef.current?.dispose();
      hostRef.current = null;
      void sessionRef.current?.end();
      sessionRef.current = null;
    },
    [setInSession],
  );

  if (!ready) return <main className="page">Loading…</main>;
  if (!auth || !wallet)
    return (
      <main className="page">
        <p>
          <Link to="/">Log in</Link> to play.
        </p>
      </main>
    );

  const powerCycle = () => {
    setShowPowerCycle(true);
  };
  const linkBusy = link.view.kind !== "idle" && link.view.kind !== "ended" && link.view.kind !== "error";
  const serverRunOver = link.view.kind === "ended" && link.view.needsReload;

  return (
    <SyncPendingReporter.Provider value={(waiting) => dispatch({ type: "syncPending", waiting })}>
      <main ref={playRef} className={`play ${fullscreenFallback ? "play-expanded" : ""}`} data-mobile-view={mobileView}>
        <MobileNavigation view={mobileView} onChange={setMobileView} />
        <ChainRail state={overlay} controls={step.kind === "playing" && <GameControls
          palette={palette} volume={hostRef.current?.audio.getVolume() ?? 0.8} touch={touch} showKeys={showKeys}
          powerDisabled={linkBusy || serverRunOver} onPower={powerCycle}
          onPalette={p => { setPalette(p); hostRef.current?.renderer.setPalette(p); }}
          onVolume={v => hostRef.current?.audio.setVolume(v)}
          onKeys={() => setShowKeys(v => !v)} onTouch={() => setTouch(v => !v)}>
          {step.offline && overlay.offlineSession && <span className="hint">{t.offline}</span>}
        </GameControls>}><Overlay state={overlay} dispatch={dispatch} compact /></ChainRail>
        <section className="stage">
          {step.kind === "register" && <RegisterStep onDone={() => setStep({ kind: "loading" })} />}
          {step.kind === "rom" && (
            <RomPicker
              lockedVersion={lockedVersion}
              onRom={(r) => {
                setRom(r);
              }}
            />
          )}
          {step.kind === "draining" && <p className="status">Uploading your last session{step.remaining ? ` (${step.remaining} left)` : ""}…</p>}
          {step.kind === "starting" && <p className="status">Loading your game{rom ? ` (${versionLabel(rom.version)})` : ""}…</p>}
          {step.kind === "error" && (
            <div className="card">
              <p className="status error">{step.message}</p>
              <button onClick={() => window.location.reload()}>Reload</button>
            </div>
          )}
          {step.kind === "superseded" && (
            <div className="card">
              <p className="status error">{t.superseded}</p>
              <button onClick={() => window.location.reload()}>Play here instead</button>
            </div>
          )}
          <div className={`game-device ${touch ? "has-touch" : ""} ${step.kind === "playing" || step.kind === "superseded" ? "" : "hidden"}`}>
          <div ref={screenRef} className={`screen ${step.kind === "playing" || step.kind === "superseded" ? "" : "hidden"}`}>
            {step.kind === "playing" && <FullscreenButton onClick={() => void toggleFullscreen()} />}
            <canvas ref={canvasRef} className="lcd" aria-label="Game screen" tabIndex={0} />
            {walletOpen && (
              <WalletMenu
                onClose={() => {
                  setWalletOpen(false);
                  hostRef.current?.closeWalletUi();
                }}
              />
            )}
            {boardOpen && !invite && !outgoing && boardKind === 1 && (
              <CableClubBattleBoard
                requirement={cableLock?.locked ? cableLock.requirement : undefined}
                battles={battles} myBattles={myBattles} trainers={trainers} notice={boardNotice}
                onClose={() => { setBoardOpen(false); setBoardNotice(undefined); hostRef.current?.closeBoardUi(); }}
                onInvite={sendInvite} onReadParty={readBoardParty}
                onRefresh={async () => { await refreshBoard(); }} onCancelListing={cancelListing}
                onPostBattle={async () => {
                  const party = await hostRef.current?.readParty();
                  if (!party) throw new Error("Game is not ready.");
                  if (party.map !== 0) await api.cableClubHeartbeat(party.map, party.trainer);
                  await api.cableClubPostBattle(party); await refreshBoard(); setBoardNotice("Battle posted. Manage it in My posts.");
                }}
                onTakeBattle={(row) => sendRequest(row.id)}
              />
            )}
            {boardOpen && !invite && !outgoing && boardKind === 2 && (
              <CableClubTradeBoard
                requirement={cableLock?.locked ? cableLock.requirement : undefined}
                trades={trades} myTrades={myTrades} trainers={trainers} notice={boardNotice}
                onClose={() => { setBoardOpen(false); setBoardNotice(undefined); hostRef.current?.closeBoardUi(); }}
                onInvite={sendInvite} onReadParty={readBoardParty}
                onRefresh={async () => { await refreshBoard(); }} onCancelListing={cancelListing}
                onPostTrade={async (slot, wants, minLevel, maxLevel) => {
                  const party = await hostRef.current?.readParty();
                  if (!party) throw new Error("Game is not ready.");
                  if (party.map !== 0) await api.cableClubHeartbeat(party.map, party.trainer);
                  await api.cableClubPostTrade({ slot, wants, minLevel, maxLevel }); await refreshBoard(); setBoardNotice("Trade posted. Manage it in My posts.");
                }}
                onTakeTrade={(row, slot) => sendRequest(row.id, slot)}
              />
            )}
            {outgoing && !invite && (
              <CableClubWaiting trainer={outgoing.trainer} connecting={outgoing.connecting} onCancel={async () => {
                await api.cableClubCancelRequest(); await refreshBoard(); setBoardNotice("Request cancelled.");
              }} />
            )}
            {invite && (
              <CableClubInvite
                key={`${invite.from}-${invite.createdAt ?? ""}`}
                name={invite.trainer} kind={invite.kind} offer={invite.offer} requested={invite.requested} levels={invite.levels}
                notice={boardNotice}
                onPause={() => hostRef.current?.holdInvite()} onDecline={declineInvite}
                onAccept={async () => {
                  const taken = await api.cableClubAcceptInvite(invite.from, invite.kind);
                  beginUnion(taken.lobbyId, invite.kind, taken.dest, () => {
                    setInvite(null); previousInvite.current = null; setBoardOpen(false); setBoardNotice(undefined);
                    hostRef.current?.closeBoardUi(); hostRef.current?.releaseInvite();
                  });
                }}
              />
            )}
          </div>
          {step.kind === "playing" && touch && hostRef.current && <TouchControls input={hostRef.current.input} disabled={mobileView !== "game" || linkBusy || boardOpen || walletOpen || !!invite || showKeys || showPowerCycle} />}
          </div>
          {step.kind === "playing" && (
            <LinkStatusBar
              view={link.view}
              result={link.result}
              onCancel={() => linkRef.current?.cancel()}
              onDismiss={() => linkRef.current?.dismiss()}
            />
          )}
        </section>
        <InventoryRail inventory={inventory} liveParty={liveParty} />
        {showKeys && hostRef.current && <ForestModal title="Game controls" onClose={() => setShowKeys(false)}>
          <KeymapEditor input={hostRef.current.input} onClose={() => setShowKeys(false)} />
        </ForestModal>}
        {showPowerCycle && <ForestModal title="Restart your game?" onClose={() => setShowPowerCycle(false)}>
          <p>Power cycling restarts the Game Boy. Progress since your last save will be lost.</p>
          <p className="hint">Save in-game first if you want to keep your latest progress.</p>
          <div className="forest-confirm-actions"><button data-modal-autofocus onClick={() => setShowPowerCycle(false)}>Keep playing</button><button className="forest-confirm-power" disabled={linkBusy || serverRunOver} onClick={() => { hostRef.current?.powerCycle(); setShowPowerCycle(false); }}>Power cycle</button></div>
        </ForestModal>}
      </main>
    </SyncPendingReporter.Provider>
  );
}
